import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { addActivity } from '../utils/cardUtils';

import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    FormGroup,
    StyledFormInput,
    StyledSelect,
    ModalActions,
    SaveButton,
    CancelButton,
    FieldLabel
} from '../styles/AddSubtaskModal.styles';

const AddSubtaskModal = ({
    isOpen,
    onClose,
    projectId,
    parentId,
    parentCardTitle,
    parentCardPriority,
    allColumns,
    columnOrder,
    onSubtaskAdded // Callback to inform CardModal
}) => {
    const { user } = useAuth();
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [selectedColumnId, setSelectedColumnId] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Reset form when modal opens
            setNewSubtaskTitle('');
            // Default to the first column in order if available, else empty
            setSelectedColumnId(columnOrder && columnOrder.length > 0 ? columnOrder[0] : '');
        }
    }, [isOpen, columnOrder]);

    const handleSaveNewSubtask = async () => {
        if (!newSubtaskTitle.trim()) {
            alert('Please enter a title for the subtask.');
            return;
        }
        if (!selectedColumnId) {
            alert('Please select a column for the subtask.');
            return;
        }

        setIsSaving(true);

        const newCardId = `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newCardObject = {
            id: newCardId,
            title: newSubtaskTitle.trim(),
            parentId: parentId,
            createdAt: new Date().toISOString(),
            description: '',
            assigneeId: null,
            labelIds: [],
            checklists: [],
            comments: [],
            activity: [],
            dueDate: null,
            isDueDateCompleted: false,
            priority: parentCardPriority || null, // Inherit parent's priority
            archived: false,
            coverImageId: null,
        };

        try {
            const projectRef = doc(db, 'projects', projectId);
            const targetColumn = allColumns[selectedColumnId];

            if (!targetColumn) {
                console.error("Target column for new subtask not found:", selectedColumnId);
                alert("Error: Target column not found. Cannot save subtask.");
                setIsSaving(false);
                return;
            }

            // Add activity to the new subtask itself
            if (user && targetColumn) {
                const subtaskActivity = addActivity(user, {
                    type: 'subtask_created',
                    details: `created subtask "${newCardObject.title}" in column "${targetColumn.title}" for parent "${parentCardTitle}"`
                });
                newCardObject.activity = [subtaskActivity];
            }

            const updatedCardsInTargetColumn = [...(targetColumn.cards || []), newCardObject];

            await updateDoc(projectRef, {
                [`columns.${selectedColumnId}.cards`]: updatedCardsInTargetColumn,
                updatedAt: new Date().toISOString(),
            });

            // Callback to CardModal to potentially update its state (e.g., parent card's activity)
            if (onSubtaskAdded) {
                onSubtaskAdded(newCardObject);
            }

            onClose(); // Close modal on success
        } catch (error) {
            console.error('Error creating new subtask:', error);
            alert('Failed to create subtask. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>Add New Subtask</ModalHeader>
                <FormGroup>
                    <FieldLabel htmlFor="subtaskTitle">Subtask Title</FieldLabel>
                    <StyledFormInput
                        id="subtaskTitle"
                        type="text"
                        placeholder="Enter subtask title..."
                        value={newSubtaskTitle}
                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                        autoFocus
                    />
                </FormGroup>
                <FormGroup>
                    <FieldLabel htmlFor="subtaskColumn">Status (Column)</FieldLabel>
                    <StyledSelect
                        id="subtaskColumn"
                        value={selectedColumnId}
                        onChange={(e) => setSelectedColumnId(e.target.value)}
                    >
                        <option value="" disabled>Select a column</option>
                        {columnOrder && allColumns && columnOrder.map(colId => {
                            const col = allColumns[colId];
                            if (!col) return null;
                            return (
                                <option key={colId} value={colId}>
                                    {col.title}
                                </option>
                            );
                        })}
                    </StyledSelect>
                </FormGroup>
                <ModalActions>
                    <CancelButton onClick={onClose} disabled={isSaving}>Cancel</CancelButton>
                    <SaveButton onClick={handleSaveNewSubtask} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Subtask'}
                    </SaveButton>
                </ModalActions>
            </ModalContent>
        </ModalOverlay>
    );
};

export default AddSubtaskModal;
